import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileText, Mail, Printer, QrCode as QrIcon, Undo2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { usePos } from "@/app/context";
import { Modal } from "@/components/Modal";
import { MoneyText } from "@/components/MoneyText";
import { useToast } from "@/components/Toast";
import { call } from "@/lib/api";
import { errorText } from "@/lib/errors";
import { openTabInTap } from "@/lib/openTab";
import { fetchReceipt, printReceipt, qrContentOf } from "@/lib/receipts";

import { ReceiptQr } from "./ReceiptQr";

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const RECEIPT_LOCALES = ["ja", "en", "th", "zh-Hant", "zh-Hans"] as const;
const LOCALE_NAME: Record<(typeof RECEIPT_LOCALES)[number], string> = {
  ja: "日本語",
  en: "English",
  th: "ไทย",
  "zh-Hant": "繁體中文",
  "zh-Hans": "简体中文",
};

/**
 * レシート (T07-25): the receipt as the customer got it (HTML preview from the server — the same
 * document the print is made from), 再発行 (the server counts it and prints 「再発行」), メール, QR
 * on the screen for the customer's phone, PDF when media-service has made it, and 返品 (T07-27).
 */
export function ReceiptScreen() {
  const { saleId = "" } = useParams();
  const { t, i18n } = useTranslation();
  const { api, db, printer } = usePos();
  const toast = useToast();
  const navigate = useNavigate();
  const [emailOpen, setEmailOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [printing, setPrinting] = useState(false);

  const sale = useQuery({
    queryKey: ["pos-sales", "detail", saleId],
    queryFn: () =>
      call(() => api.GET("/v1/pos/orders/{sale_id}", { params: { path: { sale_id: saleId } } })),
    enabled: Boolean(saleId),
  });
  const html = useQuery({
    queryKey: ["pos-receipts", "detail", saleId, "html", i18n.resolvedLanguage],
    queryFn: () => fetchReceipt(api, saleId, { format: "html", locale: i18n.resolvedLanguage }),
    enabled: Boolean(saleId),
    staleTime: 60_000,
  });
  const json = useQuery({
    queryKey: ["pos-receipts", "detail", saleId, "json"],
    queryFn: () => fetchReceipt(api, saleId, { format: "json" }),
    enabled: qrOpen,
    staleTime: Infinity,
  });

  const reprint = async () => {
    setPrinting(true);
    const outcome = await printReceipt({
      api,
      db,
      printer,
      saleId,
      label: sale.data?.sale.receiptNo ?? saleId,
    });
    setPrinting(false);
    if (outcome.error) {
      toast.show(errorText(outcome.error, t), "error");
    } else if (outcome.job?.status === "done") {
      toast.show(t("done.printed"), "success");
    } else {
      toast.show(t(`print.${outcome.job?.lastError ?? "write_failed"}`), "error");
    }
    void html.refetch();
  };

  const openPdf = async () => {
    // the tab is opened inside the tap (iPad Safari blocks window.open after an await)
    const tab = openTabInTap();
    try {
      const pdf = await fetchReceipt(api, saleId, { format: "pdf" });
      if (pdf.pdf?.status === "issued" && pdf.pdf.url) {
        if (!tab.show(pdf.pdf.url)) {
          window.open(pdf.pdf.url, "_blank", "noopener");
        }
      } else {
        tab.close();
        toast.show(
          t(pdf.pdf?.status === "unsupported" ? "receipt.pdf_unsupported" : "receipt.pdf_pending"),
        );
      }
    } catch (error) {
      tab.close();
      toast.show(errorText(error, t), "error");
    }
  };

  const info = sale.data;
  return (
    <div className="grid h-full min-h-0 grid-cols-[minmax(0,1fr)_20rem] gap-4 p-4">
      <section aria-labelledby="receipt-title" className="flex min-h-0 flex-col gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void navigate(-1)}
            className="flex min-h-pos-touch items-center gap-2 rounded-md border px-4"
          >
            <ArrowLeft aria-hidden />
            {t("app.back")}
          </button>
          <h1 id="receipt-title" className="text-2xl font-semibold">
            {t("receipt.title")}{" "}
            {info ? <span className="font-mono text-lg">{info.sale.receiptNo}</span> : null}
          </h1>
          {info ? (
            <MoneyText amount={info.order.totals.total} size="lg" className="ml-auto" />
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-hidden rounded-md border bg-white">
          {html.data?.html ? (
            // the server's own document (same as the print) — sandboxed, no scripts
            <iframe
              title={t("receipt.preview")}
              srcDoc={html.data.html}
              sandbox=""
              className="size-full"
            />
          ) : (
            <p className="p-6 text-muted-foreground">
              {html.error ? errorText(html.error, t) : t("app.loading")}
            </p>
          )}
        </div>
      </section>
      <aside className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => void reprint()}
          disabled={printing}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md bg-primary text-lg font-semibold text-primary-foreground disabled:opacity-50"
        >
          <Printer aria-hidden />
          {t("receipt.reprint")}
        </button>
        <p className="text-sm text-muted-foreground">{t("receipt.reprint_note")}</p>
        {typeof html.data?.reprintCount === "number" && html.data.reprintCount > 0 ? (
          <p className="text-sm">{t("receipt.copies", { count: html.data.reprintCount + 1 })}</p>
        ) : null}
        <button
          type="button"
          onClick={() => setEmailOpen(true)}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-lg"
        >
          <Mail aria-hidden />
          {t("receipt.email")}
        </button>
        <button
          type="button"
          onClick={() => setQrOpen(true)}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-lg"
        >
          <QrIcon aria-hidden />
          {t("receipt.qr")}
        </button>
        <button
          type="button"
          onClick={() => void openPdf()}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-lg"
        >
          <FileText aria-hidden />
          {t("receipt.pdf")}
        </button>
        <button
          type="button"
          onClick={() => void navigate(`/returns?sale=${saleId}`)}
          className="mt-auto flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-lg"
        >
          <Undo2 aria-hidden />
          {t("receipt.return")}
        </button>
      </aside>
      <EmailDialog open={emailOpen} saleId={saleId} onClose={() => setEmailOpen(false)} />
      <Modal open={qrOpen} title={t("receipt.qr_title")} onClose={() => setQrOpen(false)}>
        <div className="flex justify-center">
          {json.data ? (
            <ReceiptQr value={qrContentOf(json.data) ?? saleId} size={280} />
          ) : (
            <p>{t("app.loading")}</p>
          )}
        </div>
      </Modal>
    </div>
  );
}

function EmailDialog({
  open,
  saleId,
  onClose,
}: {
  open: boolean;
  saleId: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { api } = usePos();
  const toast = useToast();
  const [to, setTo] = useState("");
  const [locale, setLocale] = useState<(typeof RECEIPT_LOCALES)[number]>(
    (RECEIPT_LOCALES as readonly string[]).includes(i18n.resolvedLanguage ?? "")
      ? (i18n.resolvedLanguage as (typeof RECEIPT_LOCALES)[number])
      : "ja",
  );
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!EMAIL.test(to.trim())) {
      return;
    }
    setBusy(true);
    try {
      const sent = await call(() =>
        api.POST("/v1/pos/orders/{sale_id}/receipt/email", {
          params: { path: { sale_id: saleId } },
          body: { email: to.trim(), locale },
        }),
      );
      toast.show(t("receipt.email_sent", { to: sent.to }), "success");
      setTo("");
      onClose();
    } catch (error) {
      toast.show(errorText(error, t), "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open={open}
      title={t("receipt.email")}
      onClose={onClose}
      busy={busy}
      footer={
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || !EMAIL.test(to.trim())}
          className="min-h-pos-touch rounded-md bg-primary px-8 text-lg font-semibold text-primary-foreground disabled:opacity-50"
        >
          {t("receipt.email")}
        </button>
      }
    >
      <div className="flex flex-col gap-3">
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder={t("tender.email_placeholder")}
          aria-label={t("tender.email_placeholder")}
          className="min-h-pos-touch rounded-md border px-3 text-lg"
        />
        <label className="flex flex-col gap-1">
          <span className="font-medium">{t("app.language")}</span>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value as (typeof RECEIPT_LOCALES)[number])}
            className="min-h-pos-touch rounded-md border px-3 text-lg"
          >
            {RECEIPT_LOCALES.map((code) => (
              <option key={code} value={code} lang={code}>
                {LOCALE_NAME[code]}
              </option>
            ))}
          </select>
        </label>
      </div>
    </Modal>
  );
}
